import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, MapPin, Bell, BellOff, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/components/ListingCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function sameUserId(a, b) {
  const left = a == null ? "" : String(a).trim();
  const right = b == null ? "" : String(b).trim();
  return Boolean(left) && Boolean(right) && left === right;
}

/**
 * CTA acheteur : Acheter / liste d'attente si réservée.
 */
export function BuyMachineButton({ listing, onUpdated, className = "", label = "Acheter cette machine" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [onWaitlist, setOnWaitlist] = useState(!!listing?.on_waitlist);
  const [waitlistCount, setWaitlistCount] = useState(listing?.waitlist_count || 0);

  const sellerId = listing?.seller_id || listing?.seller?.id;
  const isOwner = user && sameUserId(user.id, sellerId);
  const status = listing?.status || "published";
  const isReserved = status === "reserved";
  const isSold = status === "sold";
  const isReservedBuyer =
    isReserved && user && listing?.sale?.buyer_id && sameUserId(user.id, listing.sale.buyer_id);

  if (isOwner) {
    if (isReserved || waitlistCount > 0) {
      return (
        <div
          className={`rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800 flex items-center gap-2 ${className}`}
          data-testid="seller-waitlist-count"
        >
          <Users className="h-4 w-4" />
          {waitlistCount} personne{waitlistCount !== 1 ? "s" : ""} intéressée{waitlistCount !== 1 ? "s" : ""}
        </div>
      );
    }
    return null;
  }

  if (isSold) return null;

  if (isReserved && isReservedBuyer) {
    return (
      <div
        className={`rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800 ${className}`}
        data-testid="listing-reserved-for-you"
      >
        Cette machine est réservée pour vous.
      </div>
    );
  }

  if (isReserved) {
    const join = async () => {
      if (!user) {
        toast.info("Connectez-vous pour rester intéressé");
        navigate("/connexion");
        return;
      }
      setBusy(true);
      try {
        const { data } = await api.post(`/listings/${listing.id}/waitlist`, { listing_mode: "sale" });
        setOnWaitlist(true);
        setWaitlistCount(data.waitlist_count || 0);
        toast.success("Vous êtes sur la liste d'attente");
        onUpdated?.(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setBusy(false);
      }
    };
    const leave = async () => {
      setBusy(true);
      try {
        const { data } = await api.delete(`/listings/${listing.id}/waitlist`);
        setOnWaitlist(false);
        setWaitlistCount(data.waitlist_count || 0);
        toast.success("Retiré de la liste d'attente");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className={`space-y-2 ${className}`}>
        <div
          className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800"
          data-testid="listing-reserved-notice"
        >
          Cette machine est actuellement réservée.
        </div>
        {onWaitlist ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={leave}
            data-testid="waitlist-leave"
            className="w-full h-11 border-matpro-border font-bold"
          >
            <BellOff className="h-4 w-4 mr-2" />
            Ne plus être intéressé
          </Button>
        ) : (
          <Button
            type="button"
            disabled={busy}
            onClick={join}
            data-testid="waitlist-join"
            className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-bold"
          >
            <Bell className="h-4 w-4 mr-2" />
            Je reste intéressé
          </Button>
        )}
      </div>
    );
  }

  const sellerName =
    listing?.seller?.company_name || listing?.seller?.full_name || "Vendeur";
  const location = [listing?.city, listing?.postal_code].filter(Boolean).join(" · ");

  const onClick = () => {
    if (!user) {
      toast.info("Connectez-vous pour envoyer une demande d'achat");
      navigate("/connexion");
      return;
    }
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/purchase-request`);
      toast.success(
        data.already_exists
          ? "Votre demande est déjà en cours"
          : "Demande d'achat envoyée au vendeur"
      );
      setOpen(false);
      if (data.conversation_id) {
        navigate(`/messages/${data.conversation_id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        data-testid={`buy-machine-${listing.id}`}
        onClick={onClick}
        className={`w-full h-11 min-h-11 md:h-12 text-sm md:text-base bg-matpro-green hover:bg-matpro-green-dark text-white font-black rounded-md shadow-sm ${className}`}
      >
        <ShoppingCart className="h-5 w-5 mr-2 shrink-0" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="buy-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">
              Confirmer votre intérêt
            </DialogTitle>
            <DialogDescription className="text-sm text-matpro-anthracite-muted pt-1">
              Vous êtes sur le point d&apos;envoyer une demande d&apos;achat au vendeur.
              Celui-ci pourra accepter, refuser ou continuer la discussion.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-matpro-border bg-matpro-bg p-4 space-y-2">
            <div className="font-heading font-bold text-matpro-anthracite line-clamp-2">
              {listing.title}
            </div>
            <div className="font-heading font-black text-2xl text-matpro-green">
              {formatPrice(listing.price)}
            </div>
            <div className="text-sm text-matpro-anthracite-muted">
              Vendeur : <span className="font-semibold text-matpro-anthracite">{sellerName}</span>
            </div>
            {location && (
              <div className="text-sm text-matpro-anthracite-muted inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> {location}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="border-matpro-border text-matpro-anthracite-muted font-bold"
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={submit}
              data-testid="buy-confirm-submit"
              className="bg-matpro-green hover:bg-matpro-green-dark text-white font-bold"
            >
              Envoyer ma demande d&apos;achat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
